/**
 * Tracking Data Extraction Utilities
 *
 * Helper functions for extracting Google Ads tracking parameters
 * and other tracking identifiers from URL and cookies.
 */

/**
 * Extract Google Click ID (gclid) from URL query string
 *
 * Google Ads adds gclid parameter to URLs for conversion tracking.
 * Example: ?gclid=TeSteR1234567890
 *
 * @returns gclid value or undefined if not present
 */
export function getGclid(): string | undefined {
  try {
    const params = new URLSearchParams(window.location.search)
    const gclid = params.get('gclid')

    // Return gclid if it exists and is not empty
    return gclid && gclid.trim().length > 0 ? gclid.trim() : undefined
  } catch (error) {
    console.warn('Failed to extract gclid from URL:', error)
    return undefined
  }
}

/**
 * Extract Google Client ID (gcid) from _gcl_aw cookie
 *
 * Google Ads stores conversion linker data in the _gcl_aw cookie.
 * Cookie format: _gcl_aw=GCL.{timestamp}.{gcid}
 * Example: _gcl_aw=GCL.1234567890.ABC123XYZ
 *
 * @returns gcid value or undefined if cookie not found or parsing fails
 */
export function getGcid(): string | undefined {
  try {
    // Get all cookies
    const cookies = document.cookie.split(';')

    // Find _gcl_aw cookie
    const gclAwCookie = cookies.find(cookie =>
      cookie.trim().startsWith('_gcl_aw=')
    )

    if (!gclAwCookie) {
      return undefined
    }

    // Extract cookie value (everything after '=')
    const cookieValue = gclAwCookie.split('=')[1]

    if (!cookieValue || cookieValue.trim().length === 0) {
      return undefined
    }

    // Parse cookie value: GCL.timestamp.gcid
    // Split by '.' and get the third part (gcid)
    const parts = cookieValue.trim().split('.')

    // Cookie should have at least 3 parts: GCL, timestamp, gcid
    if (parts.length >= 3) {
      const gcid = parts[2]
      return gcid && gcid.length > 0 ? gcid : undefined
    }

    return undefined
  } catch (error) {
    console.warn('Failed to extract gcid from _gcl_aw cookie:', error)
    return undefined
  }
}

/**
 * Clean session ID by removing "sess_" prefix
 *
 * Converts internal session ID format to external transaction ID format.
 * Example: "sess_abc123xyz" -> "abc123xyz"
 *
 * @param sessionId Session ID with or without "sess_" prefix
 * @returns Session ID without prefix (external_transaction_id)
 */
export function cleanSessionId(sessionId: string): string {
  if (!sessionId) {
    return ''
  }

  return sessionId.replace(/^sess_/, '').trim()
}

/**
 * Extract Facebook Browser ID (fbp) from _fbp cookie
 *
 * Facebook Pixel stores the browser identifier in the _fbp cookie.
 * Cookie format: fb.{subdomain}.{timestamp}.{random}
 * Example: _fbp=fb.1.1234567890.1234567890
 *
 * @returns fbp value or undefined if cookie not found
 */
export function getFbp(): string | undefined {
  try {
    // Get all cookies
    const cookies = document.cookie.split(';')

    // Find _fbp cookie
    const fbpCookie = cookies.find(cookie =>
      cookie.trim().startsWith('_fbp=')
    )

    if (!fbpCookie) {
      return undefined
    }

    // Extract cookie value (everything after '=')
    const cookieValue = fbpCookie.split('=')[1]

    if (!cookieValue || cookieValue.trim().length === 0) {
      return undefined
    }

    return cookieValue.trim()
  } catch (error) {
    console.warn('Failed to extract fbp from _fbp cookie:', error)
    return undefined
  }
}

/**
 * Extract Facebook Click ID (fbc) from _fbc cookie or construct from fbclid URL parameter
 *
 * Facebook Pixel stores the click identifier in the _fbc cookie.
 * Cookie format: fb.{subdomain}.{timestamp}.{fbclid}
 * Example: _fbc=fb.1.1234567890.IwAR1abc123xyz
 *
 * If _fbc cookie is not present, attempts to construct it from fbclid URL parameter.
 * Constructed format: fb.1.{current_timestamp}.{fbclid}
 *
 * @returns fbc value or undefined if cookie not found and fbclid not in URL
 */
export function getFbc(): string | undefined {
  try {
    // Try to get _fbc cookie first
    const cookies = document.cookie.split(';')
    const fbcCookie = cookies.find(cookie =>
      cookie.trim().startsWith('_fbc=')
    )

    if (fbcCookie) {
      const cookieValue = fbcCookie.split('=')[1]
      if (cookieValue && cookieValue.trim().length > 0) {
        return cookieValue.trim()
      }
    }

    // Fallback: construct from fbclid URL parameter
    const params = new URLSearchParams(window.location.search)
    const fbclid = params.get('fbclid')

    if (fbclid && fbclid.trim().length > 0) {
      // Construct fbc in the same format Facebook Pixel uses
      // Format: fb.1.<timestamp>.<fbclid>
      const timestamp = Math.floor(Date.now() / 1000) // seconds
      return `fb.1.${timestamp}.${fbclid.trim()}`
    }

    return undefined
  } catch (error) {
    console.warn('Failed to extract fbc from cookie or URL:', error)
    return undefined
  }
}

/**
 * Extract Facebook Campaign ID from URL parameters
 *
 * Looks for mcid (primary) or campaignId (fallback) in URL parameters.
 * Example: ?mcid=120238153448240268 or ?campaignId=211
 *
 * @returns fb_campaign_id value or undefined if not present
 */
export function getFbCampaignId(): string | undefined {
  try {
    const params = new URLSearchParams(window.location.search)

    // Priority: mcid > campaignId
    const mcid = params.get('mcid')
    if (mcid && mcid.trim().length > 0) {
      return mcid.trim()
    }

    const campaignId = params.get('campaignId')
    if (campaignId && campaignId.trim().length > 0) {
      return campaignId.trim()
    }

    return undefined
  } catch (error) {
    console.warn('Failed to extract Facebook campaign ID from URL:', error)
    return undefined
  }
}

/**
 * Extract Facebook Adset ID from URL parameters
 *
 * Looks for mgid in URL parameters.
 * Example: ?mgid=120238153448320268
 *
 * @returns fb_adset_id value or undefined if not present
 */
export function getFbAdsetId(): string | undefined {
  try {
    const params = new URLSearchParams(window.location.search)
    const mgid = params.get('mgid')

    return mgid && mgid.trim().length > 0 ? mgid.trim() : undefined
  } catch (error) {
    console.warn('Failed to extract Facebook adset ID from URL:', error)
    return undefined
  }
}

/**
 * Extract Facebook Ad ID from URL parameters
 *
 * Looks for maid in URL parameters.
 * Example: ?maid=120238153448260268
 *
 * @returns fb_ad_id value or undefined if not present
 */
export function getFbAdId(): string | undefined {
  try {
    const params = new URLSearchParams(window.location.search)
    const maid = params.get('maid')

    return maid && maid.trim().length > 0 ? maid.trim() : undefined
  } catch (error) {
    console.warn('Failed to extract Facebook ad ID from URL:', error)
    return undefined
  }
}

/**
 * Extract Facebook Adgroup from URL parameters
 *
 * Looks for utm_adgroup in URL parameters.
 * Example: ?utm_adgroup=Static+Ads+-+Prime+Qualified
 *
 * @returns fb_adgroup value or undefined if not present
 */
export function getFbAdgroup(): string | undefined {
  try {
    const params = new URLSearchParams(window.location.search)
    const adgroup = params.get('utm_adgroup')

    return adgroup && adgroup.trim().length > 0 ? adgroup.trim() : undefined
  } catch (error) {
    console.warn('Failed to extract Facebook adgroup from URL:', error)
    return undefined
  }
}

/**
 * Extract Facebook Creative from URL parameters
 *
 * Looks for utm_creative in URL parameters.
 * Example: ?utm_creative=Rate+-+Static+-+Colorblock
 *
 * @returns fb_creative value or undefined if not present
 */
export function getFbCreative(): string | undefined {
  try {
    const params = new URLSearchParams(window.location.search)
    const creative = params.get('utm_creative')

    return creative && creative.trim().length > 0 ? creative.trim() : undefined
  } catch (error) {
    console.warn('Failed to extract Facebook creative from URL:', error)
    return undefined
  }
}

/**
 * Extract all tracking parameters in one call
 *
 * Convenience function to get all tracking identifiers at once.
 *
 * @param sessionId Current session ID
 * @returns Object with all tracking parameters
 */
export function getAllTrackingParams(sessionId: string): {
  gclid?: string
  gcid?: string
  fbp?: string
  fbc?: string
  fb_campaign_id?: string
  fb_adset_id?: string
  fb_ad_id?: string
  fb_adgroup?: string
  fb_creative?: string
  external_transaction_id: string
} {
  return {
    gclid: getGclid(),
    gcid: getGcid(),
    fbp: getFbp(),
    fbc: getFbc(),
    fb_campaign_id: getFbCampaignId(),
    fb_adset_id: getFbAdsetId(),
    fb_ad_id: getFbAdId(),
    fb_adgroup: getFbAdgroup(),
    fb_creative: getFbCreative(),
    external_transaction_id: cleanSessionId(sessionId)
  }
}
